import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Table, { type TableColumn, type TablePagination } from '../../components/ui/Table';
import storageSyncService, {
  type StorageSyncItem,
} from '../../services/storageSync.service';
import VirtualizedPdf from '../../components/pdf/VirtualizedPdf';

const cardStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--color-gray-200)',
  borderRadius: 10,
  padding: 12,
};
const pdfWorkerPublicPath = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/pdf.worker.min.js`;
if (typeof window !== 'undefined' && pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerPublicPath) {
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerPublicPath;
}

const StorageSyncPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20; // Fixed page size per requirement.

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState<any>(null);
  const [previewCaseId, setPreviewCaseId] = useState<string>('');
  const [previewFileIndex, setPreviewFileIndex] = useState(0);
  const [previewFileBlobUrl, setPreviewFileBlobUrl] = useState<string | null>(null);
  const [previewFileMimeType, setPreviewFileMimeType] = useState<string>('');
  const [previewFileLoading, setPreviewFileLoading] = useState(false);
  const [previewFileError, setPreviewFileError] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPdfNumPages, setPreviewPdfNumPages] = useState(0);
  const [previewPdfPage, setPreviewPdfPage] = useState(1);
  const previewPdfScrollRef = useRef<HTMLDivElement | null>(null);
  const previewPdfPageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const reportQuery = useQuery({
    queryKey: ['storage-sync-report', q, missingOnly, page, limit],
    queryFn: () =>
      storageSyncService.getReport({
        q: q.trim() || undefined,
        missing_only: missingOnly || undefined,
        page,
        limit,
      }),
  });

  const syncMutation = useMutation({
    mutationFn: async (caseId: string) => storageSyncService.syncCase(caseId),
    onSuccess: () => {
      toast.success('Case synced successfully');
      queryClient.invalidateQueries({ queryKey: ['storage-sync-report'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to sync case');
    },
  });

  const dryRunCaseMutation = useMutation({
    mutationFn: async (caseId: string) => storageSyncService.dryRunCase(caseId),
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Dry-run failed');
    },
  });

  const syncAllMutation = useMutation({
    mutationFn: async () =>
      storageSyncService.syncAll({
        q: q.trim() || undefined,
        missing_only: missingOnly || undefined,
      }),
    onSuccess: (res: any) => {
      toast.success(
        `Bulk sync done (created: ${res?.data?.data?.total_created ?? 0})`
      );
      queryClient.invalidateQueries({ queryKey: ['storage-sync-report'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Bulk sync failed');
    },
  });

  const items: StorageSyncItem[] = reportQuery.data?.items ?? [];
  // Table hides pagination when `pagination.totalPages` is missing.
  // Provide a safe pagination object (same approach as `SearchDocumentsPage`).
  const serverPagination = reportQuery.data?.pagination;
  const effectivePage = serverPagination?.page ?? page;
  const effectiveLimit = serverPagination?.limit ?? limit;
  const effectiveTotal = serverPagination?.total ?? items.length;
  const effectiveTotalPages =
    serverPagination?.totalPages ??
    Math.max(1, Math.ceil(effectiveTotal / effectiveLimit));

  const pagination: TablePagination = {
    page: effectivePage,
    limit: effectiveLimit,
    total: effectiveTotal,
    totalPages: effectiveTotalPages,
    hasNextPage: effectivePage < effectiveTotalPages,
    hasPrevPage: effectivePage > 1,
  };

  const totals = useMemo(() => {
    if (reportQuery.data?.totals) return reportQuery.data.totals;
    // Fallback (should not be used once backend totals are available).
    return items.reduce(
      (acc, item) => {
        acc.db += item.db_document_count || 0;
        acc.storage += item.storage_document_count || 0;
        acc.missing += item.missing_document_count || 0;
        return acc;
      },
      { db: 0, storage: 0, missing: 0 }
    );
  }, [items, reportQuery.data?.totals]);

  const openCasePreview = async (caseId: string) => {
    setPreviewCaseId(caseId);
    const res = await dryRunCaseMutation.mutateAsync(caseId);
    setPreviewPayload(res?.data?.data ?? null);
    setPreviewFileIndex(0);
    setPreviewFileBlobUrl(null);
    setPreviewFileMimeType('');
    setPreviewFileError(null);
    setPreviewOpen(true);
    setPreviewZoom(1);
    setPreviewPdfNumPages(0);
    setPreviewPdfPage(1);
    previewPdfPageRefs.current = {};
  };

  const previewFiles = useMemo(
    () =>
      Array.isArray(previewPayload?.would_create)
        ? previewPayload.would_create
        : [],
    [previewPayload]
  );

  const activePreviewFile =
    previewFiles.length > 0 ? previewFiles[Math.max(0, Math.min(previewFileIndex, previewFiles.length - 1))] : null;
  const isPreviewPdf = previewFileMimeType === 'application/pdf' || previewFileMimeType.includes('pdf');
  const isPreviewImage = previewFileMimeType.startsWith('image/');
  const totalPreviewPages = isPreviewPdf ? previewPdfNumPages : isPreviewImage ? 1 : 0;

  React.useEffect(() => {
    const relativePath = activePreviewFile?.relative_path;
    if (!previewOpen || !relativePath) {
      setPreviewFileLoading(false);
      setPreviewFileError(null);
      if (previewFileBlobUrl) {
        URL.revokeObjectURL(previewFileBlobUrl);
        setPreviewFileBlobUrl(null);
      }
      return;
    }

    let cancelled = false;
    setPreviewFileLoading(true);
    setPreviewFileError(null);

    storageSyncService
      .getStorageFilePreview(relativePath)
      .then(({ blobUrl, mimeType }) => {
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        if (previewFileBlobUrl) URL.revokeObjectURL(previewFileBlobUrl);
        setPreviewFileBlobUrl(blobUrl);
        setPreviewFileMimeType(mimeType || '');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setPreviewFileError(err?.response?.data?.message || 'Failed to load preview.');
        setPreviewFileBlobUrl(null);
        setPreviewFileMimeType('');
      })
      .finally(() => {
        if (!cancelled) setPreviewFileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [previewOpen, activePreviewFile?.relative_path]);

  React.useEffect(() => {
    return () => {
      if (previewFileBlobUrl) URL.revokeObjectURL(previewFileBlobUrl);
    };
  }, [previewFileBlobUrl]);

  useEffect(() => {
    if (totalPreviewPages <= 0) {
      setPreviewPdfPage(1);
      return;
    }
    setPreviewPdfPage((p) => Math.min(Math.max(1, p), totalPreviewPages));
  }, [totalPreviewPages]);

  const goToPreviewPage = (next: number) => {
    if (!isPreviewPdf || totalPreviewPages <= 0) return;
    const page = Math.min(Math.max(1, next), totalPreviewPages);
    setPreviewPdfPage(page);
    requestAnimationFrame(() => {
      const container = previewPdfScrollRef.current;
      const target = previewPdfPageRefs.current[page];
      if (!container || !target) return;
      container.scrollTo({ top: Math.max(0, target.offsetTop - 8), behavior: 'smooth' });
    });
  };

  const handlePreviewPdfScroll = () => {
    if (!isPreviewPdf || previewPdfNumPages <= 0 || !previewPdfScrollRef.current) return;
    const markerY = previewPdfScrollRef.current.scrollTop + 80;
    let visible = 1;
    for (let p = 1; p <= previewPdfNumPages; p += 1) {
      const el = previewPdfPageRefs.current[p];
      if (!el) continue;
      if (el.offsetTop <= markerY) visible = p;
      else break;
    }
    if (visible !== previewPdfPage) {
      setPreviewPdfPage(visible);
    }
  };

  const blockContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const columns: TableColumn<StorageSyncItem>[] = [
    {
      key: 'cms_number',
      header: 'CMS Number',
      minWidth: 200,
      tooltip: (r) => r.cms_number || '—',
      render: (r) => <span style={{ fontWeight: 700 }}>{r.cms_number || '—'}</span>,
    },
    {
      key: 'year',
      header: 'Year',
      minWidth: 90,
      tooltip: (r) => String(r.year ?? '—'),
      render: (r) => r.year ?? '—',
    },
    {
      key: 'db',
      header: 'DB Count',
      minWidth: 100,
      tooltip: (r) => String(r.db_document_count ?? 0),
      render: (r) => r.db_document_count ?? 0,
    },
    {
      key: 'storage',
      header: 'Storage Count',
      minWidth: 120,
      tooltip: (r) => String(r.storage_document_count ?? 0),
      render: (r) => r.storage_document_count ?? 0,
    },
    {
      key: 'missing',
      header: 'Missing',
      minWidth: 90,
      tooltip: (r) => String(r.missing_document_count ?? 0),
      render: (r) => (
        <span style={{ fontWeight: 700 }}>{r.missing_document_count ?? 0}</span>
      ),
    },
    {
      key: 'root',
      header: 'Storage Root',
      minWidth: 380,
      clampLines: 2,
      tooltip: (r) => r.case_storage_root || '—',
      render: (r) => (
        <span style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
          {r.case_storage_root || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Action',
      minWidth: 220,
      tooltip: () => 'Actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={row.missing_document_count <= 0 || dryRunCaseMutation.isPending}
            onClick={() => openCasePreview(row.case_id)}
            style={{
              border: '1px solid var(--color-gray-300)',
              background: 'white',
              color: 'var(--color-gray-700)',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: row.missing_document_count > 0 ? 'pointer' : 'not-allowed',
              fontSize: 12,
            }}
          >
            Preview
          </button>
          <button
            type="button"
            disabled={row.missing_document_count <= 0 || syncMutation.isPending}
            onClick={() => syncMutation.mutate(row.case_id)}
            style={{
              border: '1px solid var(--color-primary)',
              background:
                row.missing_document_count > 0
                  ? 'var(--color-primary)'
                  : 'var(--color-gray-200)',
              color: row.missing_document_count > 0 ? 'white' : '#666',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: row.missing_document_count > 0 ? 'pointer' : 'not-allowed',
              fontSize: 12,
            }}
          >
            Sync Missing
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Storage Sync"
        subtitle="Compare database documents with storage files and sync missing entries"
      />

      <div
        style={{
          ...cardStyle,
          marginBottom: 12,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search by CMS number..."
          style={{
            flex: 1,
            minWidth: 220,
            border: '1px solid var(--color-gray-300)',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 13,
          }}
        />
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => {
              setMissingOnly(e.target.checked);
              setPage(1);
            }}
          />
          Missing only
        </label>
        <button
          type="button"
          onClick={() => syncAllMutation.mutate()}
          disabled={syncAllMutation.isPending}
          style={{
            border: '1px solid var(--color-primary)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Sync All
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={cardStyle}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
            DB Documents
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{totals.db}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
            Storage Documents
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{totals.storage}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
            Missing In DB
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color:
                totals.missing > 0 ? 'var(--color-warning)' : 'var(--color-success)',
            }}
          >
            {totals.missing}
          </div>
        </div>
      </div>

      {reportQuery.isLoading ? (
        <div style={cardStyle}>Loading sync report...</div>
      ) : reportQuery.isError ? (
        <div style={{ ...cardStyle, color: 'var(--color-error)' }}>
          Failed to load storage sync report.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '65vh',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          <Table
            columns={columns}
            rows={items}
            rowKey={(r) => r.case_id}
            minWidth={1100}
            stickyHeader
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
            emptyState={
              <div style={{ padding: 20, color: 'var(--color-gray-500)' }}>
                No cases found.
              </div>
            }
          />
        </div>
      )}

      {previewOpen && (
        <>
          <div
            role="presentation"
            onClick={() => setPreviewOpen(false)}
            className="fixed inset-0 z-[1200] bg-black/40"
            onContextMenu={blockContextMenu}
          />
          <div
            className="fixed left-1/2 top-1/2 z-[1201] h-[88vh] w-[min(1200px,98vw)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-4 shadow-xl"
            onContextMenu={blockContextMenu}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 700 }}>Document Preview</div>
                <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
                  {`Case: ${previewPayload?.cms_number || previewCaseId}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13 }}>
                Would create: <b>{previewPayload?.would_create_count ?? 0}</b>
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 8,
                position: 'relative',
              }}
              onContextMenu={blockContextMenu}
            >
              {activePreviewFile ? (
                <div style={{ padding: 10 }}>
                  {previewFileBlobUrl && !previewFileLoading && !previewFileError && (isPreviewPdf || isPreviewImage) && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        borderBottom: '1px solid var(--color-gray-200)',
                        background: 'var(--color-gray-50)',
                        padding: '8px 10px',
                        marginBottom: 10,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-gray-600)' }}>Zoom</span>
                      <button type="button" onClick={() => setPreviewZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} style={{ border: '1px solid var(--color-gray-200)', background: 'white', borderRadius: 6, padding: 4 }}>
                        <ZoomOut size={14} />
                      </button>
                      <span style={{ minWidth: 42, textAlign: 'center', fontSize: 12, fontWeight: 600 }}>{Math.round(previewZoom * 100)}%</span>
                      <button type="button" onClick={() => setPreviewZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} style={{ border: '1px solid var(--color-gray-200)', background: 'white', borderRadius: 6, padding: 4 }}>
                        <ZoomIn size={14} />
                      </button>
                      <button type="button" onClick={() => setPreviewZoom(1)} style={{ border: '1px solid var(--color-gray-200)', background: 'white', borderRadius: 6, padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <RotateCcw size={13} />
                        Reset
                      </button>
                      {totalPreviewPages > 0 && (
                        <>
                          <span style={{ margin: '0 4px', color: 'var(--color-gray-400)' }}>|</span>
                          <button type="button" onClick={() => goToPreviewPage(previewPdfPage - 1)} disabled={!isPreviewPdf || previewPdfPage <= 1} style={{ border: '1px solid var(--color-gray-200)', background: 'white', borderRadius: 6, padding: 4 }}>
                            <ChevronLeft size={14} />
                          </button>
                          <span
                            style={{
                              minWidth: 58,
                              border: '1px solid var(--color-gray-200)',
                              borderRadius: 6,
                              textAlign: 'center',
                              fontSize: 12,
                              padding: '3px 6px',
                              background: 'white',
                              fontWeight: 600,
                            }}
                          >
                            {previewPdfPage} / {totalPreviewPages}
                          </span>
                          <button type="button" onClick={() => goToPreviewPage(previewPdfPage + 1)} disabled={!isPreviewPdf || previewPdfPage >= totalPreviewPages} style={{ border: '1px solid var(--color-gray-200)', background: 'white', borderRadius: 6, padding: 4 }}>
                            <ChevronRight size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {previewFileLoading ? (
                    <div style={{ padding: 20, color: 'var(--color-gray-500)' }}>Loading preview...</div>
                  ) : previewFileError ? (
                    <div style={{ padding: 20, color: 'var(--color-error)' }}>{previewFileError}</div>
                  ) : previewFileBlobUrl && isPreviewImage ? (
                    <img
                      src={previewFileBlobUrl}
                      alt={activePreviewFile.file_name || 'Preview'}
                      style={{ width: `${Math.max(100, previewZoom * 100)}%`, maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }}
                    />
                  ) : previewFileBlobUrl && isPreviewPdf ? (
                    <div
                      ref={previewPdfScrollRef}
                      onScroll={handlePreviewPdfScroll}
                      data-pdf-scroll-root="true"
                      style={{
                        height: '70vh',
                        overflow: 'auto',
                        background: 'var(--color-gray-200)',
                        padding: 10,
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <VirtualizedPdf
                          file={previewFileBlobUrl}
                          scale={previewZoom}
                          currentPage={previewPdfPage}
                          pageClassName="shadow-sm"
                          onLoadSuccess={({ numPages }) => {
                            setPreviewPdfNumPages(numPages || 0);
                            setPreviewPdfPage((p) => Math.min(Math.max(1, p), numPages || 1));
                          }}
                          onLoadError={() => {
                            setPreviewPdfNumPages(0);
                            setPreviewPdfPage(1);
                          }}
                          onPageWrapperRef={(pageNumber, el) => {
                            previewPdfPageRefs.current[pageNumber] = el;
                          }}
                          onCurrentPageChange={(page) => {
                            if (page !== previewPdfPage) setPreviewPdfPage(page);
                          }}
                          config={{ rootMarginPx: 1800, estimatedPageHeightPx: 1100 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 20 }}>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        Preview not supported for this file type.
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginBottom: 12 }}>
                        {activePreviewFile.file_name}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          storageSyncService.downloadStorageFile(
                            activePreviewFile.relative_path,
                            activePreviewFile.file_name
                          )
                        }
                        style={{
                          border: '1px solid var(--color-primary)',
                          background: 'var(--color-primary)',
                          color: 'white',
                          borderRadius: 8,
                          padding: '8px 10px',
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <pre style={{ margin: 0, padding: 10, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(previewPayload, null, 2)}
                </pre>
              )}

              {previewFiles.length > 1 && previewFileIndex < previewFiles.length - 1 && (
                <button
                  type="button"
                  onClick={() => setPreviewFileIndex((v) => Math.min(previewFiles.length - 1, v + 1))}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: '1px solid var(--color-gray-300)',
                    background: 'white',
                    borderRadius: 999,
                    width: 28,
                    height: 28,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                  title="Next file"
                >
                  &gt;
                </button>
              )}
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                style={{
                  border: '1px solid var(--color-gray-300)',
                  background: 'white',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setPreviewOpen(false);
                  if (previewCaseId) syncMutation.mutate(previewCaseId);
                }}
                style={{
                  border: '1px solid var(--color-primary)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Confirm Sync
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StorageSyncPage;

